import type { SupportLocalMessage, SupportMessageDTO, SupportThreadDTO } from '../types/support';
import { clearAttachmentMediaCache } from './supportAttachmentMediaCache';

/** Версия схемы кэша — увеличивать при несовместимых изменениях. */
export const SUPPORT_CHAT_CACHE_VERSION = 1;

/** Сколько последних сообщений хранить локально. */
export const SUPPORT_CHAT_CACHE_LIMIT = 20;

/** Максимальный возраст кэша (7 дней). */
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const STORAGE_PREFIX = 'support-chat-cache';

export interface SupportChatCachePayload {
  version: number;
  userId: string | number;
  threadId: string;
  thread: SupportThreadDTO;
  messages: SupportMessageDTO[];
  /** Есть ли сообщения старше самого раннего закэшированного. */
  hasMore: boolean;
  cachedAt: string;
}

const storageKey = (userId: string | number): string =>
  `${STORAGE_PREFIX}:v${SUPPORT_CHAT_CACHE_VERSION}:${userId}`;

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/** Преобразует локальное сообщение в сериализуемый DTO (только подтверждённые). */
export const toCacheableMessage = (message: SupportLocalMessage): SupportMessageDTO | null => {
  if (message.status !== 'sent' || !message.id || !message.threadId) return null;
  return {
    id: message.id,
    threadId: message.threadId,
    direction: message.direction,
    text: message.text,
    createdAt: message.createdAt,
    telegramAdminUsername: message.telegramAdminUsername,
    attachments: message.attachments,
  };
};

/** Выбирает хвост истории для сохранения и корректный hasMore. */
export const pickMessagesForCache = (
  messages: SupportLocalMessage[],
  hasMore: boolean,
): { messages: SupportMessageDTO[]; hasMore: boolean } => {
  const sent = messages
    .map(toCacheableMessage)
    .filter((m): m is SupportMessageDTO => m !== null);

  const tail = sent.slice(-SUPPORT_CHAT_CACHE_LIMIT);
  const truncated = sent.length > SUPPORT_CHAT_CACHE_LIMIT;

  return {
    messages: tail,
    hasMore: hasMore || truncated,
  };
};

/** ID вложений из последних N сообщений. */
export const collectAttachmentIdsForCache = (
  messages: SupportLocalMessage[],
  hasMore = false,
): Set<string> => {
  const { messages: tail } = pickMessagesForCache(messages, hasMore);
  const ids = new Set<string>();
  for (const message of tail) {
    for (const attachment of message.attachments ?? []) {
      if (attachment.id) ids.add(attachment.id);
    }
  }
  return ids;
};

export const readSupportChatCache = (userId: string | number): SupportChatCachePayload | null => {
  if (!isBrowser()) return null;

  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as SupportChatCachePayload;
    if (parsed.version !== SUPPORT_CHAT_CACHE_VERSION) {
      localStorage.removeItem(storageKey(userId));
      return null;
    }
    if (parsed.userId !== userId) return null;
    if (!parsed.threadId || !parsed.thread || !Array.isArray(parsed.messages)) return null;

    const age = Date.now() - new Date(parsed.cachedAt).getTime();
    if (Number.isNaN(age) || age > CACHE_TTL_MS) {
      localStorage.removeItem(storageKey(userId));
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const writeSupportChatCache = (
  userId: string | number,
  thread: SupportThreadDTO,
  messages: SupportLocalMessage[],
  hasMore: boolean,
): void => {
  if (!isBrowser() || !thread.id) return;

  const { messages: cachedMessages, hasMore: cachedHasMore } = pickMessagesForCache(
    messages,
    hasMore,
  );

  if (cachedMessages.length === 0) {
    clearSupportChatCache(userId);
    return;
  }

  const payload: SupportChatCachePayload = {
    version: SUPPORT_CHAT_CACHE_VERSION,
    userId,
    threadId: thread.id,
    thread,
    messages: cachedMessages,
    hasMore: cachedHasMore,
    cachedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(payload));
  } catch {
    // localStorage может быть переполнен — не блокируем UI
  }
};

export const clearSupportChatCache = (userId?: string | number): void => {
  if (!isBrowser()) return;

  try {
    if (userId != null) {
      localStorage.removeItem(storageKey(userId));
      return;
    }

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(`${STORAGE_PREFIX}:`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

/** Очищает текстовый кэш (localStorage) и медиа (IndexedDB). */
export const clearAllSupportChatCaches = (userId?: string | number): void => {
  clearSupportChatCache(userId);
  void clearAttachmentMediaCache(userId);
};
